import { CMSPage } from '@/types/cms'
import { CMSMachineDetails, CMSRexCommission } from '@/types/machines'
import { CMSPlanDetail } from '@/types/plans'
import { fetchWithRetry } from '@/utils/fetchWithRetry'
import { normalizeCMSData } from '@/utils/normalizeCMSData'

const token = process.env.NEXT_PUBLIC_CMS_API_READ_TOKEN
const envName = process.env.NEXT_PUBLIC_ENVIRONMENT_NAME

const defaultHeaders = {
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  'Content-Type': 'application/json',
  Accept: 'application/json'
}

export const PROMO_ID = 'promo-landing'

/*
O tempo de resposta aumenta conforme a profundidade de dados solicitada.
- A consulta no nível 7 leva em media 5s.
- A consulta no nível 8 leva em media 25s.
- Solicitar níveis mais profundos (acima de 8) pode causar a indisponibilidade da aplicação.
*/
const populateDeep = 7

const getIsDraft = (draft?: boolean) => draft || envName === 'preprd'

export const GetBy = (path: string) => {
  return fetch(
    `${
      process.env.NEXT_PUBLIC_CMS_API_PATH
    }/api/${path}?pLevel=${populateDeep}${
      getIsDraft() ? '&status=draft' : '&status=published'
    }`,
    {
      method: 'GET',
      headers: defaultHeaders
    }
  )
}

export const GetDataByCMS = async (path: string, draft?: boolean) => {
  const response = await fetchWithRetry(
    `${
      process.env.NEXT_PUBLIC_CMS_API_PATH
    }/api/sites/?filters[pageName][$eqi]=${path}&pLevel=${populateDeep}${
      getIsDraft(draft) ? '&status=draft' : '&status=published'
    }`,
    {
      method: 'GET',
      headers: defaultHeaders
    }
  )

  if (!response.ok) {
    throw new Error(
      `GetDataByCMS: fetch failed with status ${response.status}, path: ${path}.`
    )
  }

  const { data } = await response.json()

  return normalizeCMSData(data[0])
}

export async function getAllCMSPagesData(
  { draft } = { draft: false }
): Promise<CMSPage[]> {
  try {
    const response = await fetch(
      `${
        process.env.NEXT_PUBLIC_CMS_API_PATH
      }/api/sites/?pagination[pageSize]=100&pLevel=${populateDeep}${
        getIsDraft(draft) ? '&status=draft' : '&status=published'
      }`,
      {
        method: 'GET',
        headers: defaultHeaders
      }
    )

    const { data } = await response.json()
    if (!data?.length) {
      return []
    }

    const allCMSPagesData = await Promise.all(data.map(normalizeCMSData))

    return allCMSPagesData
  } catch (err) {
    console.error(err)
    throw new Error('Required Fail')
  }
}

export const GetCouponByCMS = async (couponUrl: string) => {
  try {
    const response = await fetch(
      `${
        process.env.NEXT_PUBLIC_CMS_API_PATH
      }/api/coupons?filters[couponCode][$eq]=${couponUrl}${
        getIsDraft() ? '&status=draft' : '&status=published'
      }`,
      {
        method: 'GET',
        headers: defaultHeaders
      }
    )
    const { data } = await response.json()

    if (data.length > 0) {
      return data[0]
    }

    return {}
  } catch (err) {
    console.error('Error coupon CMS:', err)
  }
}

export const GetCountdownByCMS = async () => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_CMS_API_PATH}/api/countdown-banners?pLevel=3${
        getIsDraft() ? '&status=draft' : '&status=published'
      }`,
      {
        method: 'GET',
        headers: defaultHeaders
      }
    )
    const { data } = await response.json()

    if (data.length > 0) {
      return data
    }

    return {}
  } catch (err) {
    console.error('Error countdown CMS:', err)
  }
}

export const GetCarouselBarByCMS = async () => {
  try {
    const response = await fetch(
      `${
        process.env.NEXT_PUBLIC_CMS_API_PATH
      }/api/carousel-bar-slides?pLevel=3${
        getIsDraft() ? '&status=draft' : '&status=published'
      }`,
      {
        method: 'GET',
        headers: defaultHeaders
      }
    )
    const { data } = await response.json()

    if (data.length > 0) {
      return normalizeCMSData(data)
    }

    return {}
  } catch (err) {
    console.error('Error carouselBar CMS:', err)
  }
}

export const GetScheduleByCMS = async () => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_CMS_API_PATH}/api/schedules?pLevel=3${
        getIsDraft() ? '&status=draft' : '&status=published'
      }`,
      {
        method: 'GET',
        headers: defaultHeaders
      }
    )
    const { data } = await response.json()

    if (data.length > 0) {
      return data
    }

    return {}
  } catch (err) {
    console.error('Error schedules CMS:', err)
  }
}

export const GetPlansByCMS = async (): Promise<CMSPlanDetail[]> => {
  const response = await fetchWithRetry(
    `${
      process.env.NEXT_PUBLIC_CMS_API_PATH
    }/api/plans?sort[0]=order:asc&pLevel=${populateDeep}${
      getIsDraft() ? '&status=draft' : '&status=published'
    }`,
    {
      method: 'GET',
      headers: defaultHeaders
    }
  )

  if (!response.ok) {
    throw new Error(
      `GetPlansByCMS: fetch failed with status ${response.status}.`
    )
  }

  const { data } = await response.json()

  return data
}

export const GetMachinesDetailsByCMS = async (): Promise<
  CMSMachineDetails[]
> => {
  const response = await fetchWithRetry(
    `${
      process.env.NEXT_PUBLIC_CMS_API_PATH
    }/api/machine-details?pLevel=${populateDeep}${
      getIsDraft() ? '&status=draft' : '&status=published'
    }`,
    {
      method: 'GET',
      headers: defaultHeaders
    }
  )

  if (!response.ok) {
    throw new Error(
      `GetMachinesDetailsByCMS: fetch failed with status ${response.status}.`
    )
  }

  const { data } = await response.json()

  return data
}

export const GetRexCommissions = async (): Promise<CMSRexCommission[]> => {
  try {
    const response = await fetch(
      `${
        process.env.NEXT_PUBLIC_CMS_API_PATH
      }/api/rex-commissions?populate[plan][fields][0]=name&populate[machine][fields][0]=refID&pagination[pageSize]=100&pagination[page]=1&pLevel=${populateDeep}${
        getIsDraft() ? '&status=draft' : '&status=published'
      }`,
      {
        method: 'GET',
        headers: defaultHeaders
      }
    )
    const { data } = await response.json()

    return data
  } catch (err) {
    console.error('Error rex commissions CMS:', err)
  }

  return []
}

export const GetMachineCatalogByCMS = async () => {
  const response = await fetchWithRetry(
    `${
      process.env.NEXT_PUBLIC_CMS_API_PATH
    }/api/machine-catalogs?pLevel=${populateDeep}${
      getIsDraft() ? '&status=draft' : '&status=published'
    }`,
    {
      method: 'GET',
      headers: defaultHeaders
    }
  )

  if (!response.ok) {
    throw new Error(
      `GetMachineCatalogByCMS: fetch failed with status ${response.status}.`
    )
  }

  const { data } = await response.json()

  return data.map((item: { name: string }) => item.name)
}
